/**
 * Vera Academy - BFF API client (`/api/academy/*`).
 *
 * All state-changing calls go through `bffFetch` (CSRF header + cookies).
 * Field names mirror the BFF response shapes (camelCase, like the store's
 * mapped rows).
 */

import { bffFetch } from '@shared/api/bffFetch';
import type { AcademySnapshot } from '@widgets/academy-content';

// ---------------------------------------------------------------------------
// Types (mirror packages/bff/src/academy/store.ts)
// ---------------------------------------------------------------------------

export type ItemKind = 'lesson' | 'article';
export type Visibility = 'all' | 'role' | 'admin';
export type ItemStatus = 'draft' | 'published' | 'archived';

export const BLOCK_KINDS = [
  'rich_text',
  'heading',
  'image',
  'video',
  'callout',
  'stat',
  'compare',
  'list',
  'file',
  'embed',
  'assessment_ref',
] as const;
export type BlockKind = (typeof BLOCK_KINDS)[number];

export interface AcademyMe {
  oid: string;
  name: string;
  canAuthor: boolean;
  canViewReports: boolean;
  /** academy.reset-quiz (Academy.Admin): may reset someone's course quiz. */
  canResetQuiz: boolean;
  canAssign: boolean;
  canWaive: boolean;
  isAdmin: boolean;
}

export interface Collection {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  icon: string | null;
  sortOrder: number;
  isActive: boolean;
}

export interface ContentItemMeta {
  id: string;
  slug: string;
  kind: ItemKind;
  title: string;
  summary: string | null;
  collectionId: string | null;
  docTypeId: string | null;
  docTypeSlug: string | null;
  tags: string[];
  visibility: Visibility;
  visibleRoles: string[];
  modules: string[];
  ownerEntraId: string | null;
  reviewDueAt: string | null;
  status: ItemStatus;
  currentVersionNo: number;
  sortOrder: number;
  disableTermLinks: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AuthorItemSummary extends ContentItemMeta {
  blockCount: number;
  collectionSlug: string | null;
  collectionTitle: string | null;
  helpRoutes: string[];
  /** Courses this item belongs to (lessons; derived from course_lesson). */
  courses: { id: string; slug: string; title: string }[];
}

export interface EditorBlock {
  id?: string;
  kind: BlockKind;
  data: Record<string, unknown>;
}

export interface VersionMeta {
  id: string;
  versionNo: number;
  isMajor: boolean;
  changeNote: string | null;
  publishedByEntraId: string | null;
  publishedAt: string;
  snapshotSha256: string;
}

export interface AuthorItemDetail {
  item: ContentItemMeta;
  blocks: (EditorBlock & { id: string; sortOrder: number })[];
  versions: VersionMeta[];
  helpRoutes: string[];
}

export interface PublishedItem {
  slug: string;
  kind: ItemKind;
  title: string;
  summary: string | null;
  tags: string[];
  modules: string[];
  collectionSlug: string | null;
  collectionTitle: string | null;
  versionNo: number;
  publishedAt: string;
  createdAt: string;
}

export interface PublishedContent {
  slug: string;
  title: string;
  kind: ItemKind;
  versionNo: number;
  publishedAt: string;
  snapshot: AcademySnapshot;
}

export interface TermRecord {
  id: string;
  slug: string;
  term: string;
  expansion: string | null;
  shortDef: string;
  longItemId: string | null;
  aliases: string[];
  tags: string[];
  isPublished: boolean;
  updatedAt: string;
}

export interface UploadedAsset {
  itemId: string;
  name: string;
  size: number;
  contentType: string;
  url: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function readError(res: Response, fallback: string): Promise<never> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  throw new Error(body.error || `${fallback} (${res.status})`);
}

async function getJson<T>(path: string, fallback: string): Promise<T> {
  const res = await bffFetch(path);
  if (!res.ok) await readError(res, fallback);
  return (await res.json()) as T;
}

async function sendJson<T>(method: string, path: string, body: unknown, fallback: string): Promise<T> {
  const res = await bffFetch(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) await readError(res, fallback);
  return (await res.json()) as T;
}

/** URL-safe slug from a human title (collection and item creation). */
export const slugify = (raw: string): string =>
  raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);

// ---------------------------------------------------------------------------
// Me
// ---------------------------------------------------------------------------

export const fetchMe = () => getJson<AcademyMe>('/api/academy/me', 'Failed to load profile');

// ---------------------------------------------------------------------------
// Collections
// ---------------------------------------------------------------------------

/**
 * The editorial vocabulary (academy migration 009). show_in_library is the
 * listing control: a Module guide is real content that simply does not shelve
 * in the Know library.
 */
export interface DocType {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  showInLibrary: boolean;
  isGoverned: boolean;
  sortOrder: number;
  isActive: boolean;
}

export const fetchDocTypes = () =>
  getJson<{ docTypes: DocType[] }>('/api/academy/doc-types', 'Failed to load document types').then(
    (r) => r.docTypes
  );

export const fetchCollections = (all = false) =>
  getJson<{ collections: Collection[] }>(
    `/api/academy/collections${all ? '?all=1' : ''}`,
    'Failed to load collections'
  ).then((r) => r.collections);

export const createCollection = (input: {
  slug: string;
  title: string;
  description?: string | null;
  sortOrder?: number;
}) =>
  sendJson<{ collection: Collection }>('POST', '/api/academy/collections', input, 'Failed to create collection').then(
    (r) => r.collection
  );

export const updateCollection = (
  id: string,
  patch: Partial<Pick<Collection, 'title' | 'description' | 'sortOrder' | 'isActive'>>
) =>
  sendJson<{ collection: Collection }>(
    'PATCH',
    `/api/academy/collections/${encodeURIComponent(id)}`,
    patch,
    'Failed to update collection'
  ).then((r) => r.collection);

// ---------------------------------------------------------------------------
// Reader
// ---------------------------------------------------------------------------

export interface ContentFilter {
  collection?: string;
  module?: string;
  tag?: string;
  q?: string;
  kind?: ItemKind;
  /**
   * One doc type by slug. ABSENT means the library: every type flagged
   * show_in_library, which leaves the module guides out. Naming
   * 'module-guide' is how they are browsed as a set.
   */
  docType?: string;
}

export const fetchPublishedItems = (filter: ContentFilter) => {
  const search = new URLSearchParams();
  if (filter.collection) search.set('collection', filter.collection);
  if (filter.module) search.set('module', filter.module);
  if (filter.tag) search.set('tag', filter.tag);
  if (filter.q) search.set('q', filter.q);
  if (filter.kind) search.set('kind', filter.kind);
  if (filter.docType) search.set('docType', filter.docType);
  const qs = search.toString();
  return getJson<{ items: PublishedItem[] }>(
    `/api/academy/content${qs ? `?${qs}` : ''}`,
    'Failed to load content'
  ).then((r) => r.items);
};

export const fetchPublishedTags = () =>
  getJson<{ tags: string[] }>('/api/academy/content/tags', 'Failed to load tags').then((r) => r.tags);

export const fetchPublishedContent = (slug: string) =>
  getJson<{ content: PublishedContent }>(
    `/api/academy/content/${encodeURIComponent(slug)}`,
    'Failed to load article'
  ).then((r) => r.content);

// ---------------------------------------------------------------------------
// Authoring
// ---------------------------------------------------------------------------

export const fetchAuthorItems = (filter: { kind?: ItemKind; status?: string; q?: string }) => {
  const search = new URLSearchParams();
  if (filter.kind) search.set('kind', filter.kind);
  if (filter.status) search.set('status', filter.status);
  if (filter.q) search.set('q', filter.q);
  const qs = search.toString();
  return getJson<{ items: AuthorItemSummary[] }>(
    `/api/academy/items${qs ? `?${qs}` : ''}`,
    'Failed to load items'
  ).then((r) => r.items);
};

export const fetchAuthorItem = (id: string) =>
  getJson<AuthorItemDetail>(`/api/academy/items/${encodeURIComponent(id)}`, 'Failed to load item');

export interface NewItem {
  slug: string;
  kind: ItemKind;
  title: string;
  summary?: string | null;
  collectionId?: string | null;
  docTypeSlug?: string | null;
  tags?: string[];
  visibility?: Visibility;
  modules?: string[];
}

export const createItem = (input: NewItem) =>
  sendJson<{ item: ContentItemMeta }>('POST', '/api/academy/items', input, 'Failed to create item').then(
    (r) => r.item
  );

export interface ItemPatch {
  slug?: string;
  title?: string;
  summary?: string | null;
  collectionId?: string | null;
  docTypeSlug?: string | null;
  tags?: string[];
  visibility?: Visibility;
  visibleRoles?: string[];
  modules?: string[];
  reviewDueAt?: string | null;
  status?: 'draft' | 'archived';
  sortOrder?: number;
  disableTermLinks?: boolean;
  helpRoutes?: string[];
}

export const updateItem = (id: string, patch: ItemPatch) =>
  sendJson<{ item: ContentItemMeta }>(
    'PATCH',
    `/api/academy/items/${encodeURIComponent(id)}`,
    patch,
    'Failed to update item'
  ).then((r) => r.item);

/** Article <-> lesson. A lesson leaves its collection; one still in a course cannot become an article. */
export const setItemKind = (id: string, kind: ItemKind) =>
  sendJson<{ item: ContentItemMeta }>(
    'PUT',
    `/api/academy/items/${encodeURIComponent(id)}/kind`,
    { kind },
    "Failed to change the item's kind"
  ).then((r) => r.item);

export const deleteItem = async (id: string) => {
  const res = await bffFetch(`/api/academy/items/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok) await readError(res, 'Failed to delete item');
};

export const saveBlocks = (itemId: string, blocks: EditorBlock[]) =>
  sendJson<{ blocks: (EditorBlock & { id: string; sortOrder: number })[] }>(
    'PUT',
    `/api/academy/items/${encodeURIComponent(itemId)}/blocks`,
    { blocks: blocks.map((b) => ({ kind: b.kind, data: b.data })) },
    'Failed to save blocks'
  ).then((r) => r.blocks);

export const publishItem = (itemId: string, input: { isMajor: boolean; changeNote?: string | null }) =>
  sendJson<{ item: ContentItemMeta; version: VersionMeta }>(
    'POST',
    `/api/academy/items/${encodeURIComponent(itemId)}/publish`,
    input,
    'Failed to publish'
  );

// ---------------------------------------------------------------------------
// Document import: one uploaded file -> a PROPOSED draft.
//
// The BFF writes nothing. Accepting a proposal goes back through createItem +
// saveBlocks above, so an import lands as an ordinary draft.
// ---------------------------------------------------------------------------

/** Block kinds an import can propose: no uploads, no allowlisted URLs. */
export const IMPORTABLE_BLOCK_KINDS = ['heading', 'rich_text', 'list', 'callout', 'stat', 'compare'] as const;

export const IMPORT_FILE_ACCEPT = '.pdf,.docx,.txt,.md,.markdown,.htm,.html,.csv';

export interface ImportedDraft {
  title: string;
  summary: string | null;
  suggestedSlug: string;
  tags: string[];
  /** What the author should check before publishing (the model's own flags). */
  notes: string[];
  blocks: EditorBlock[];
}

export interface ImportResult {
  draft: ImportedDraft;
  source: { fileName: string; kind: 'pdf' | 'word' | 'text'; truncated: boolean };
  /** Blocks the model returned that failed server-side validation. */
  dropped: number;
  model: string;
  usage: { input_tokens: number; output_tokens: number };
}

export async function importDocument(file: File): Promise<ImportResult> {
  const form = new FormData();
  form.append('file', file);
  const res = await bffFetch('/api/academy/import/document', { method: 'POST', body: form });
  if (!res.ok) await readError(res, 'Could not read that document');
  return (await res.json()) as ImportResult;
}

// ---------------------------------------------------------------------------
// Glossary
// ---------------------------------------------------------------------------

export const fetchTerms = () =>
  getJson<{ terms: TermRecord[] }>('/api/academy/terms', 'Failed to load glossary').then((r) => r.terms);

export const fetchAllTerms = () =>
  getJson<{ terms: TermRecord[] }>('/api/academy/terms/all', 'Failed to load glossary').then((r) => r.terms);

export interface TermInput {
  slug: string;
  term: string;
  expansion?: string | null;
  shortDef: string;
  aliases?: string[];
  tags?: string[];
  isPublished?: boolean;
}

export const createTerm = (input: TermInput) =>
  sendJson<{ term: TermRecord }>('POST', '/api/academy/terms', input, 'Failed to create term').then((r) => r.term);

export const updateTerm = (id: string, patch: Partial<TermInput>) =>
  sendJson<{ term: TermRecord }>(
    'PATCH',
    `/api/academy/terms/${encodeURIComponent(id)}`,
    patch,
    'Failed to update term'
  ).then((r) => r.term);

export const deleteTerm = async (id: string) => {
  const res = await bffFetch(`/api/academy/terms/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok) await readError(res, 'Failed to delete term');
};

/**
 * Downloads the whole glossary as CSV. The file is built by the BFF, not from
 * the loaded term list, so the authors-only rule is enforced server side; it
 * is fetched as a blob rather than navigated to so a 403 surfaces as an error
 * instead of replacing the page.
 */
export async function downloadGlossaryCsv(): Promise<string> {
  const res = await bffFetch('/api/academy/terms/export.csv');
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Export failed (${res.status})`);
  }
  const named = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '');
  const fileName = named ? named[1]! : 'glossary.csv';
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return fileName;
}

// ---------------------------------------------------------------------------
// Assets
// ---------------------------------------------------------------------------

/** Ceiling enforced again server-side; this just fails fast in the editor. */
export const VIDEO_MAX_BYTES = 500 * 1024 * 1024;

export const VIDEO_ACCEPT = 'video/mp4,video/webm,video/quicktime,video/x-m4v';

/**
 * Upload a video in chunks.
 *
 * Video does not go through `uploadAsset`: that route caps at 10MB and buffers
 * the whole file. Here the BFF opens a Graph upload session, we feed it slices
 * in order, and the last chunk comes back with the committed asset. Chunks are
 * sequential by contract - the server refuses one that does not start where
 * the last ended - so this loop must stay serial.
 */
export async function uploadVideoAsset(
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<UploadedAsset> {
  if (file.size > VIDEO_MAX_BYTES) {
    throw new Error(
      `That video is ${Math.round(file.size / (1024 * 1024))} MB, over the ${Math.round(
        VIDEO_MAX_BYTES / (1024 * 1024),
      )} MB limit. Compress it first.`,
    );
  }

  const startRes = await bffFetch('/api/academy/assets/video/session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename: file.name, contentType: file.type, size: file.size }),
  });
  if (!startRes.ok) await readError(startRes, 'Could not start the upload');
  const { uploadId, chunkBytes } = (await startRes.json()) as {
    uploadId: string;
    chunkBytes: number;
  };

  onProgress?.(0);
  for (let offset = 0; offset < file.size; offset += chunkBytes) {
    const slice = file.slice(offset, Math.min(offset + chunkBytes, file.size));
    const res = await bffFetch(`/api/academy/assets/video/session/${uploadId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: slice,
    });
    if (!res.ok) await readError(res, 'Upload failed');
    const result = (await res.json()) as { asset: UploadedAsset | null; received: number };
    onProgress?.(Math.min(result.received / file.size, 1));
    if (result.asset) return result.asset;
  }
  throw new Error('Upload finished without a committed file. Try again.');
}

export async function uploadAsset(file: File): Promise<UploadedAsset> {
  const form = new FormData();
  form.append('file', file);
  const res = await bffFetch('/api/academy/assets', { method: 'POST', body: form });
  if (!res.ok) await readError(res, 'Upload failed');
  return ((await res.json()) as { asset: UploadedAsset }).asset;
}

// ---------------------------------------------------------------------------
// Learn (PLAN 6.1-6.3): catalog, enrollment, lessons, course authoring
// ---------------------------------------------------------------------------

export interface EnrollmentMeta {
  id: string;
  courseId: string;
  courseVersionId: string;
  courseVersionNo: number;
  status: 'not_started' | 'in_progress' | 'completed' | 'expired' | 'waived';
  startedAt: string | null;
  completedAt: string | null;
  completedRequired: number;
  totalRequired: number;
}

export interface CatalogCourse {
  slug: string;
  title: string;
  description: string | null;
  category: string | null;
  coverImageUrl: string | null;
  estimatedMinutes: number | null;
  versionNo: number;
  lessonCount: number;
  enrollment: EnrollmentMeta | null;
}

export interface CatalogPath {
  slug: string;
  title: string;
  description: string | null;
  courses: { slug: string; title: string; required: boolean }[];
}

export interface CourseMeta {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  category: string | null;
  coverImageUrl: string | null;
  estimatedMinutes: number | null;
  ownerEntraId: string | null;
  status: 'draft' | 'published' | 'archived';
  currentVersionNo: number;
  recertMonths: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ViewerLesson {
  itemId: string;
  slug: string;
  title: string;
  required: boolean;
  order: number;
  progress: 'none' | 'viewed' | 'completed';
}

export interface CourseForViewer {
  course: CourseMeta;
  versionNo: number;
  lessons: ViewerLesson[];
  enrollment: EnrollmentMeta | null;
}

export interface LessonForViewer {
  courseSlug: string;
  courseTitle: string;
  itemSlug: string;
  title: string;
  snapshot: AcademySnapshot;
  contentVersionNo: number;
  enrolled: boolean;
  progress: 'none' | 'viewed' | 'completed';
  prevSlug: string | null;
  nextSlug: string | null;
}

export const fetchCatalog = () =>
  getJson<{ courses: CatalogCourse[]; paths: CatalogPath[] }>('/api/academy/learn/catalog', 'Failed to load catalog');

export const fetchLearnCourse = (slug: string) =>
  getJson<CourseForViewer>(`/api/academy/learn/courses/${encodeURIComponent(slug)}`, 'Failed to load course');

export const enrollInCourse = (slug: string) =>
  sendJson<{ enrollmentId: string; upgraded: boolean }>(
    'POST',
    `/api/academy/learn/courses/${encodeURIComponent(slug)}/enroll`,
    {},
    'Failed to enroll'
  );

export const fetchLesson = (courseSlug: string, itemSlug: string) =>
  getJson<{ lesson: LessonForViewer }>(
    `/api/academy/learn/courses/${encodeURIComponent(courseSlug)}/lessons/${encodeURIComponent(itemSlug)}`,
    'Failed to load lesson'
  ).then((r) => r.lesson);

export const markLessonProgress = (courseSlug: string, itemSlug: string, complete: boolean) =>
  sendJson<{ ok: boolean; courseCompleted: boolean }>(
    'POST',
    `/api/academy/learn/courses/${encodeURIComponent(courseSlug)}/lessons/${encodeURIComponent(itemSlug)}/progress`,
    { complete },
    'Failed to record progress'
  );

export interface AuthorCourse extends CourseMeta {
  lessonCount: number;
}

export interface CourseLessonRow {
  itemId: string;
  slug: string;
  title: string;
  itemStatus: string;
  currentVersionNo: number;
  isRequired: boolean;
  sortOrder: number;
}

export interface AuthorCourseDetail {
  course: CourseMeta;
  lessons: CourseLessonRow[];
  versions: { versionNo: number; isMajor: boolean; publishedAt: string }[];
}

export const fetchAuthorCourses = () =>
  getJson<{ courses: AuthorCourse[] }>('/api/academy/courses', 'Failed to list courses').then((r) => r.courses);

export const fetchAuthorCourse = (id: string) =>
  getJson<AuthorCourseDetail>(`/api/academy/courses/${encodeURIComponent(id)}`, 'Failed to load course');

export interface CourseInput {
  slug: string;
  title: string;
  description?: string | null;
  category?: string | null;
  estimatedMinutes?: number | null;
  recertMonths?: number | null;
}

export const createCourse = (input: CourseInput) =>
  sendJson<{ course: CourseMeta }>('POST', '/api/academy/courses', input, 'Failed to create course').then(
    (r) => r.course
  );

export const updateCourse = (id: string, patch: Partial<CourseInput> & { status?: 'draft' | 'archived' }) =>
  sendJson<{ course: CourseMeta }>(
    'PATCH',
    `/api/academy/courses/${encodeURIComponent(id)}`,
    patch,
    'Failed to update course'
  ).then((r) => r.course);

export const deleteCourse = async (id: string) => {
  const res = await bffFetch(`/api/academy/courses/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok) await readError(res, 'Failed to delete course');
};

export const setCourseLessons = (id: string, lessons: { itemId: string; isRequired: boolean }[]) =>
  sendJson<{ ok: boolean }>(
    'PUT',
    `/api/academy/courses/${encodeURIComponent(id)}/lessons`,
    { lessons },
    'Failed to set lessons'
  );

export const publishCourse = (id: string, isMajor: boolean) =>
  sendJson<{ versionNo: number; lessonSetChanged: boolean; learnersOnOlderVersion: number }>(
    'POST',
    `/api/academy/courses/${encodeURIComponent(id)}/publish`,
    { isMajor },
    'Failed to publish course'
  );

export interface PathMeta {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  status: string;
  sortOrder: number;
}

export const fetchPaths = () =>
  getJson<{ paths: PathMeta[] }>('/api/academy/paths', 'Failed to list paths').then((r) => r.paths);

export const createPath = (input: { slug: string; title: string; description?: string | null; status?: string }) =>
  sendJson<{ path: PathMeta }>('POST', '/api/academy/paths', input, 'Failed to create path').then((r) => r.path);

export const setPathCourses = (
  id: string,
  courses: { courseId: string; isRequired: boolean; unlocksAfterPrior: boolean }[]
) =>
  sendJson<{ ok: boolean }>(
    'PUT',
    `/api/academy/paths/${encodeURIComponent(id)}/courses`,
    { courses },
    'Failed to set path courses'
  );

// ---------------------------------------------------------------------------
// Reporting (Manager/Admin)
// ---------------------------------------------------------------------------

export interface CourseReportRow {
  courseSlug: string;
  courseTitle: string;
  enrollments: number;
  people: number;
  inProgress: number;
  completed: number;
  completedLast30d: number;
}

export interface EnrollmentReportRow {
  personName: string | null;
  personEmail: string | null;
  personId: string;
  courseSlug: string;
  courseTitle: string;
  courseVersionNo: number;
  status: string;
  source: string;
  enrolledAt: string;
  startedAt: string | null;
  completedAt: string | null;
  completedRequired: number;
  totalRequired: number;
  hasQuiz: boolean;
  quizPassed: boolean | null;
}

export const fetchReportSummary = () =>
  getJson<{ courses: CourseReportRow[] }>('/api/academy/reports/summary', 'Failed to load report').then(
    (r) => r.courses
  );

export const fetchReportEnrollments = (courseSlug?: string) =>
  getJson<{ enrollments: EnrollmentReportRow[] }>(
    `/api/academy/reports/enrollments${courseSlug ? `?course=${encodeURIComponent(courseSlug)}` : ''}`,
    'Failed to load report'
  ).then((r) => r.enrollments);

// ---------------------------------------------------------------------------
// Admin: quiz reset (academy.reset-quiz). Voiding keeps the attempt rows and
// stamps them; deleting removes them. Either way the audit row survives.
// ---------------------------------------------------------------------------

export type QuizResetMode = 'void' | 'delete';

export interface QuizAttemptRow {
  attemptNo: number;
  startedAt: string;
  submittedAt: string | null;
  scorePct: number | null;
  passed: boolean | null;
  assessmentVersionNo: number;
  voidedAt: string | null;
  voidReason: string | null;
}

export interface QuizResetTarget {
  courseSlug: string;
  courseTitle: string;
  personEntraId: string;
  personName: string | null;
  personEmail: string | null;
  hasQuiz: boolean;
  liveAttempts: number;
  passed: boolean;
  completedEnrollments: number;
  liveCertificates: number;
  attempts: QuizAttemptRow[];
}

export interface QuizResetInput {
  courseSlug: string;
  personEntraId: string;
  mode: QuizResetMode;
  reopenEnrollment: boolean;
  reason?: string | null;
}

export interface QuizResetResult {
  courseTitle: string;
  attemptsAffected: number;
  reopenedEnrollments: number;
  certificatesRevoked: number;
}

export interface QuizResetLogRow {
  id: string;
  courseSlug: string;
  courseTitle: string;
  personEntraId: string;
  personName: string | null;
  mode: QuizResetMode;
  attemptsAffected: number;
  reopenedEnrollment: boolean;
  certificateRevoked: boolean;
  reason: string | null;
  resetByEntraId: string;
  createdAt: string;
}

export const fetchQuizResetTarget = (courseSlug: string, personEntraId: string) =>
  getJson<{ target: QuizResetTarget }>(
    `/api/academy/admin/quiz-target?course=${encodeURIComponent(courseSlug)}&person=${encodeURIComponent(personEntraId)}`,
    'Failed to load quiz history'
  ).then((r) => r.target);

export const resetQuiz = (input: QuizResetInput) =>
  sendJson<QuizResetResult>('POST', '/api/academy/admin/quiz-reset', input, 'Failed to reset the quiz');

export const fetchQuizResets = (filter: { course?: string; person?: string; limit?: number } = {}) => {
  const search = new URLSearchParams();
  if (filter.course) search.set('course', filter.course);
  if (filter.person) search.set('person', filter.person);
  if (filter.limit) search.set('limit', String(filter.limit));
  const qs = search.toString();
  return getJson<{ resets: QuizResetLogRow[] }>(
    `/api/academy/admin/quiz-resets${qs ? `?${qs}` : ''}`,
    'Failed to load the reset log'
  ).then((r) => r.resets);
};

// ---------------------------------------------------------------------------
// Assessments (PLAN 6.4): quiz authoring + the learner attempt flow.
// The take payload NEVER carries the answer key; grading is server-side.
// ---------------------------------------------------------------------------

export const QUESTION_KINDS = ['single_choice', 'multi_choice', 'true_false', 'short_text'] as const;
export type QuestionKind = (typeof QUESTION_KINDS)[number];
export type ShowAnswersAfter = 'never' | 'pass' | 'always';

export interface AuthorOption {
  id?: string;
  label: string;
  isCorrect: boolean;
}

export interface AuthorQuestion {
  id?: string;
  kind: QuestionKind;
  stem: string;
  explanation: string | null;
  points: number;
  options: AuthorOption[];
}

export interface AuthorAssessment {
  id: string;
  courseId: string | null;
  title: string;
  passScorePct: number;
  maxAttempts: number | null;
  cooldownMinutes: number;
  shuffleQuestions: boolean;
  shuffleOptions: boolean;
  showAnswersAfter: ShowAnswersAfter;
  isGraded: boolean;
  status: string;
  currentVersionNo: number;
  questions: AuthorQuestion[];
}

export interface AssessmentInput {
  title?: string;
  passScorePct?: number;
  maxAttempts?: number | null;
  cooldownMinutes?: number;
  shuffleQuestions?: boolean;
  shuffleOptions?: boolean;
  showAnswersAfter?: ShowAnswersAfter;
}

export const fetchCourseAssessment = (courseId: string) =>
  getJson<{ assessment: AuthorAssessment | null }>(
    `/api/academy/courses/${encodeURIComponent(courseId)}/assessment`,
    'Failed to load quiz'
  ).then((r) => r.assessment);

export const upsertCourseAssessment = (courseId: string, input: AssessmentInput) =>
  sendJson<{ assessment: AuthorAssessment }>(
    'PUT',
    `/api/academy/courses/${encodeURIComponent(courseId)}/assessment`,
    input,
    'Failed to save quiz'
  ).then((r) => r.assessment);

export const saveAssessmentQuestions = (
  assessmentId: string,
  questions: {
    kind: QuestionKind;
    stem: string;
    explanation?: string | null;
    points?: number;
    options: { label: string; isCorrect: boolean }[];
  }[]
) =>
  sendJson<{ assessment: AuthorAssessment }>(
    'PUT',
    `/api/academy/assessments/${encodeURIComponent(assessmentId)}/questions`,
    { questions },
    'Failed to save questions'
  ).then((r) => r.assessment);

export const deleteAssessment = async (id: string) => {
  const res = await bffFetch(`/api/academy/assessments/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok) await readError(res, 'Failed to delete quiz');
};

// Learner flow ---------------------------------------------------------------

export interface TakeQuestion {
  id: string;
  kind: QuestionKind;
  stem: string;
  points: number;
  options: { id: string; label: string }[];
}

export interface ReviewQuestion {
  id: string;
  kind: QuestionKind;
  stem: string;
  explanation: string | null;
  points: number;
  options: { id: string; label: string; isCorrect: boolean }[];
  selectedOptionIds: string[];
  textValue: string | null;
  correct: boolean;
}

export interface AttemptSummary {
  attemptNo: number;
  startedAt: string;
  submittedAt: string | null;
  scorePct: number | null;
  passed: boolean | null;
}

export interface QuizStatus {
  title: string;
  questionCount: number;
  pointsPossible: number;
  passScorePct: number;
  maxAttempts: number | null;
  cooldownMinutes: number;
  showAnswersAfter: ShowAnswersAfter;
  isGraded: boolean;
  enrolled: boolean;
  attemptsUsed: number;
  inFlightAttemptId: string | null;
  bestScorePct: number | null;
  passed: boolean;
  canStart: boolean;
  nextAttemptAt: string | null;
  attempts: AttemptSummary[];
  review: ReviewQuestion[] | null;
}

export interface StartResult {
  attemptId: string;
  attemptNo: number;
  questions: TakeQuestion[];
}

export interface SubmitResult {
  scorePct: number;
  passed: boolean;
  pointsEarned: number;
  pointsPossible: number;
  passScorePct: number;
  courseCompleted: boolean;
  certificateSerial: string | null;
  review: ReviewQuestion[] | null;
  perQuestion: { questionId: string; correct: boolean; pointsAwarded: number }[];
}

export const fetchQuizStatus = (courseSlug: string) =>
  getJson<{ assessment: QuizStatus | null }>(
    `/api/academy/learn/courses/${encodeURIComponent(courseSlug)}/quiz`,
    'Failed to load quiz'
  ).then((r) => r.assessment);

export const startQuizAttempt = (courseSlug: string) =>
  sendJson<StartResult>(
    'POST',
    `/api/academy/learn/courses/${encodeURIComponent(courseSlug)}/quiz/start`,
    {},
    'Failed to start the quiz'
  );

export const submitQuizAttempt = (
  attemptId: string,
  answers: { questionId: string; selectedOptionIds?: string[]; textValue?: string }[]
) =>
  sendJson<SubmitResult>(
    'POST',
    `/api/academy/learn/attempts/${encodeURIComponent(attemptId)}/submit`,
    { answers },
    'Failed to submit the quiz'
  );

// Certificates ---------------------------------------------------------------

export interface CertificateDetail {
  serial: string;
  personName: string | null;
  personEmail: string | null;
  personEntraId: string;
  courseSlug: string;
  courseTitle: string;
  courseVersionNo: number;
  scorePct: number | null;
  completedAt: string | null;
  issuedAt: string;
  revokedAt: string | null;
}

export const fetchMyCertificates = () =>
  getJson<{ certificates: CertificateDetail[] }>('/api/academy/learn/certificates', 'Failed to list certificates').then(
    (r) => r.certificates
  );

export const fetchCertificate = (serial: string) =>
  getJson<{ certificate: CertificateDetail }>(
    `/api/academy/learn/certificates/${encodeURIComponent(serial)}`,
    'Failed to load certificate'
  ).then((r) => r.certificate);

export const fetchCourseCertificate = (courseSlug: string) =>
  getJson<{ certificate: CertificateDetail | null }>(
    `/api/academy/learn/courses/${encodeURIComponent(courseSlug)}/certificate`,
    'Failed to load certificate'
  ).then((r) => r.certificate);

// ---------------------------------------------------------------------------
// Assignment (Phase 4a)
// ---------------------------------------------------------------------------

export type AssignmentStatus = 'open' | 'completed' | 'overdue' | 'waived' | 'revoked';

export interface MyAssignment {
  id: string;
  courseSlug: string;
  courseTitle: string;
  courseCategory: string | null;
  estimatedMinutes: number | null;
  cycle: number;
  source: 'manual' | 'recert';
  dueAt: string | null;
  status: AssignmentStatus;
  overdue: boolean;
  assignedByName: string | null;
  assignedAt: string;
  completedRequired: number;
  totalRequired: number;
}

export interface Assignment {
  id: string;
  personEntraId: string;
  personName: string | null;
  personEmail: string | null;
  courseId: string;
  courseSlug: string;
  courseTitle: string;
  courseCategory: string | null;
  cycle: number;
  source: 'manual' | 'recert';
  assignedByName: string | null;
  assignedAt: string;
  dueAt: string | null;
  status: AssignmentStatus;
  completedAt: string | null;
  waivedReason: string | null;
  enrollmentId: string | null;
}

export interface ResolvedCourse {
  id: string;
  slug: string;
  title: string;
  category: string | null;
}

export interface AssignRequest {
  people: { oid: string; name?: string | null; email?: string | null }[];
  courseSlugs?: string[];
  categories?: string[];
  dueAt?: string | null;
}

export const fetchMyAssignments = () =>
  getJson<{ assignments: MyAssignment[] }>(
    '/api/academy/my/assignments',
    'Failed to load your assigned training'
  ).then((r) => r.assignments);

export const fetchMyAssignmentCount = () =>
  getJson<{ open: number; overdue: number }>(
    '/api/academy/my/assignment-count',
    'Failed to load your assignment count'
  );

/** What a course + category selection actually expands to, before assigning. */
export const resolveAssignmentCourses = (courseSlugs: string[], categories: string[]) =>
  getJson<{ courses: ResolvedCourse[] }>(
    `/api/academy/admin/assignments/resolve?courses=${encodeURIComponent(courseSlugs.join(','))}` +
      `&categories=${encodeURIComponent(categories.join(','))}`,
    'Failed to resolve courses'
  ).then((r) => r.courses);

export const fetchAssignments = (courseSlug?: string) =>
  getJson<{ assignments: Assignment[] }>(
    `/api/academy/admin/assignments${courseSlug ? `?course=${encodeURIComponent(courseSlug)}` : ''}`,
    'Failed to load assignments'
  ).then((r) => r.assignments);

export const createAssignments = (body: AssignRequest) =>
  sendJson<{ created: number; updated: number; courses: number; people: number }>(
    'POST',
    '/api/academy/admin/assignments',
    body,
    'Failed to assign training'
  );

export const waiveAssignment = (input: { id: string; reason: string }) =>
  sendJson<{ assignment: Assignment }>(
    'POST',
    `/api/academy/admin/assignments/${encodeURIComponent(input.id)}/waive`,
    { reason: input.reason },
    'Failed to waive'
  );

export const revokeAssignment = (input: { id: string; reason: string }) =>
  sendJson<{ ok: true }>(
    'POST',
    `/api/academy/admin/assignments/${encodeURIComponent(input.id)}/revoke`,
    { reason: input.reason },
    'Failed to revoke'
  );

export const runAssignmentSweeps = () =>
  sendJson<{ summary: { overdue: number; recertCreated: number; mailed: number; mailFailed: number } }>(
    'POST',
    '/api/academy/admin/assignments/run-sweeps',
    {},
    'Sweep failed'
  );
